// Universal prompt engine: builds initial values, validates dynamic fields and
// compiles a {{placeholder}} template. Works for all 48 records — no hardcoding.

const ARRAY_COMPONENTS = new Set(["multiselect", "tags"]);

export function initialValues(fields = []) {
  const v = {};
  for (const f of fields) {
    // Wymagane listy startują puste — użytkownik musi świadomie wybrać opcję.
    if (f.component === "select" && f.required) {
      v[f.id] = "";
      continue;
    }
    // Próbnik koloru startuje pusty — dopóki użytkownik świadomie nie wybierze
    // koloru, pole jest „niewypełnione" (inaczej reguła „podaj kolor lub HEX/RAL"
    // byłaby zawsze spełniona przez domyślną wartość <input type=color>).
    if (f.component === "color") {
      v[f.id] = "";
      continue;
    }
    if (f.defaultValue !== null && f.defaultValue !== undefined) {
      v[f.id] = f.defaultValue;
    } else if (ARRAY_COMPONENTS.has(f.component)) {
      v[f.id] = [];
    } else {
      v[f.id] = "";
    }
  }
  return v;
}

export function fieldDisplayValue(field, value) {
  return displayValue(field, value);
}

function displayValue(field, value) {
  if (value === null || value === undefined) return "";
  switch (field.component) {
    case "select": {
      const opt = (field.options || []).find((o) => o.value === value);
      if (!opt) return String(value);
      // promptValue lets a template inject a grammatically-correct form
      // (e.g. genitive "ściany za sofą") while the dropdown shows the label.
      return opt.promptValue ?? opt.label;
    }
    case "multiselect": {
      const arr = Array.isArray(value) ? value : [];
      return arr
        .map((val) => {
          const opt = (field.options || []).find((o) => o.value === val);
          if (!opt) return val;
          return opt.promptValue ?? opt.label;
        })
        .join(", ");
    }
    case "tags":
      return Array.isArray(value) ? value.join(", ") : "";
    case "checkbox":
      return value === true ? (field.trueLabel || "Tak") : (field.falseLabel || "");
    default:
      return String(value).trim();
  }
}

// Strip {{#if field}}...{{/if}} blocks whose field is empty. Non-nested.
function stripConditionals(template, byId, values) {
  return template.replace(
    /\{\{#if\s+([\w.-]+)\s*\}\}([\s\S]*?)\{\{\/if\}\}/g,
    (_, id, content) => {
      const field = byId[id];
      const empty = !field || isEmpty(values[id], field.component);
      return empty ? "" : content;
    },
  );
}

export function compileTemplate(template = "", fields = [], values = {}) {
  const byId = {};
  for (const f of fields) byId[f.id] = f;
  let out = stripConditionals(template, byId, values);
  out = out.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, id) => {
    const field = byId[id];
    if (!field) return "";
    return displayValue(field, values[id]);
  });
  // Tidy artefacts left by empty optional values so the final prompt reads clean:
  out = out
    .replace(/\{\{[^}]*\}\}/g, "")        // safety: drop any stray placeholder
    .replace(/\(\s*\)/g, "")               // empty parentheses
    .replace(/[ \t]{2,}/g, " ")            // collapse runs of spaces/tabs
    .replace(/[ \t]+([,.;:!?])/g, "$1")    // no space before punctuation
    .replace(/([,;:])\s*(?=[,.;:!?])/g, "") // drop a separator glued to another
    .replace(/([([])\s*,\s*/g, "$1")       // stray comma right after an opener
    .replace(/[ \t]+\n/g, "\n")            // trailing spaces before newline
    .trim();
  return out;
}

function isEmpty(value, component) {
  if (ARRAY_COMPONENTS.has(component)) return !Array.isArray(value) || value.length === 0;
  if (component === "checkbox") return value !== true;
  return value === null || value === undefined || String(value).trim() === "";
}

export function validateField(field, value) {
  const v = field.validation || {};
  const empty = isEmpty(value, field.component);

  if (field.required && empty) {
    return v.requiredMessage || "To pole jest wymagane.";
  }
  if (empty) return null; // optional & empty → ok

  // string length
  if (["text", "textarea", "url", "color"].includes(field.component)) {
    const str = String(value);
    if (v.minLength && str.length < v.minLength) return v.lengthMessage || `Minimum ${v.minLength} znaków.`;
    if (v.maxLength && str.length > v.maxLength) return v.lengthMessage || `Maksimum ${v.maxLength} znaków.`;
    if (v.pattern) {
      try {
        if (!new RegExp(v.pattern).test(str)) return v.patternMessage || "Nieprawidłowy format.";
      } catch (e) { /* ignore bad pattern */ }
    }
  }

  if (field.component === "number") {
    const num = Number(value);
    if (Number.isNaN(num)) return v.rangeMessage || "Podaj liczbę.";
    if (v.min !== undefined && num < v.min) return v.rangeMessage || `Minimum ${v.min}.`;
    if (v.max !== undefined && num > v.max) return v.rangeMessage || `Maksimum ${v.max}.`;
  }

  if (ARRAY_COMPONENTS.has(field.component)) {
    const arr = Array.isArray(value) ? value : [];
    if (v.minItems && arr.length < v.minItems) return v.itemsMessage || `Wybierz co najmniej ${v.minItems}.`;
    if (v.maxItems && arr.length > v.maxItems) return v.itemsMessage || `Maksimum ${v.maxItems}.`;
  }

  return null;
}

export function validateAll(fields = [], values = {}, groups = []) {
  const errors = {};
  for (const f of fields) {
    const err = validateField(f, values[f.id]);
    if (err) errors[f.id] = err;
  }
  // cross-field "at least one of" groups
  for (const g of groups) {
    const ids = g.anyOf || [];
    const field = (id) => fields.find((f) => f.id === id);
    const satisfied = ids.some((id) => {
      const f = field(id);
      return f && !isEmpty(values[id], f.component);
    });
    if (!satisfied) {
      const msg = g.message || "Uzupełnij przynajmniej jedno z tych pól.";
      for (const id of ids) {
        if (!errors[id]) errors[id] = msg;
      }
    }
  }
  return errors;
}
